// Status system: burn / shock / slow / vulnerable / stun / overheated on enemies.
// Stacking rules live in statusRules.ts (pure); this file applies them to live enemies,
// ticks damage over time through the damage system, draws small periodic visuals at
// afflicted enemies and implements WILDFIRE (burn spreads on death).

import type { EnemyApi, FxApi, GameContext } from '../../core/contracts';
import type { StatusApplication } from '../../core/types';
import { COLORS } from '../../config/palette';
import { enemyList, flag, targetable, type DeathSnapshot, type StatusSystem } from './internal';
import type { CombatSfx } from './sfx';
import {
  BURN_MAX_STACKS,
  ST_IGNITED,
  ST_OVERHEATED,
  ST_STUNNED,
  STATUS_DEFAULTS,
  advanceStatuses,
  applyStatusRule,
} from './statusRules';

/** WILDFIRE: burn spreads to enemies within this many px of the victim's edge. */
const WILDFIRE_RADIUS = 190;
const BURN_TAGS = ['burn'] as const;

interface Tracked {
  /** Cosmetic timers (seconds until the next puff). */
  emberT: number;
  sparkT: number;
  stunT: number;
  slowT: number;
  smokeT: number;
}

export function createStatus(ctx: GameContext, fx: FxApi, sfx: CombatSfx): StatusSystem {
  const tracked = new Map<EnemyApi, Tracked>();

  const track = (e: EnemyApi): void => {
    if (!tracked.has(e)) tracked.set(e, { emberT: 0, sparkT: Math.random() * 0.2, stunT: 0, slowT: 0.2, smokeT: 0 });
  };

  /** Bubble text above an enemy when a status "pops" (ignite / stun / overheat). */
  const popText = (e: EnemyApi, text: string, color: number): void => {
    fx.floatText(e.x, e.y - e.radius - 18, text, { color, size: 20, duration: 650 });
  };

  const api: StatusSystem = {
    apply(target, s) {
      if (!target || !target.alive || !s || !STATUS_DEFAULTS[s.id]) return;
      const ev = applyStatusRule(target.statuses, s, target.kind === 'boss');
      if (target.statuses.size > 0) track(target);
      if (ev & ST_IGNITED) {
        sfx.play('burn_ignite', { volume: 0.5, x: target.x }, 90);
        fx.embers(target.x, target.y, { count: 6, color: COLORS.burn });
      }
      if (ev & ST_STUNNED) {
        sfx.play('chain_zap', { volume: 0.55, pitch: 1.35, x: target.x }, 80);
        fx.sparks(target.x, target.y - target.radius * 0.6, -Math.PI / 2, { count: 10, color: COLORS.electric, speed: 260, spread: Math.PI * 2, life: 320 });
        popText(target, 'STUNNED', COLORS.electric);
      }
      if (ev & ST_OVERHEATED) {
        fx.smoke(target.x, target.y, { count: 3, color: 0x3a2a22, scale: 0.7 });
        popText(target, 'OVERHEATED', COLORS.heat);
      }
    },

    has(target, id) {
      return !!target?.statuses.has(id);
    },

    stacks(target, id) {
      return target?.statuses.get(id)?.stacks ?? 0;
    },

    clear(target) {
      if (!target) return;
      target.statuses.clear();
      tracked.delete(target);
    },

    onKilled(target, snap) {
      tracked.delete(target);
      if (snap.burnStacks <= 0 || !flag(ctx, 'WILDFIRE')) return;
      // The fire jumps to every nearby enemy with the victim's stacks (at least 2 so it catches).
      const spread: StatusApplication = {
        id: 'burn',
        stacks: Math.max(2, Math.min(BURN_MAX_STACKS, snap.burnStacks)),
        duration: Math.max(2.5, snap.burnRemaining),
        potency: snap.burnPotency > 0 ? snap.burnPotency : STATUS_DEFAULTS.burn.potency,
      };
      let any = false;
      for (const e of enemyList(ctx)) {
        if (e === target || !targetable(e)) continue;
        const d = Math.hypot(e.x - target.x, e.y - target.y) - e.radius;
        if (d > WILDFIRE_RADIUS) continue;
        api.apply(e, spread);
        fx.beam(target.x, target.y, e.x, e.y, { color: COLORS.burn, width: 5, duration: 220 });
        fx.embers(e.x, e.y, { count: 5, color: COLORS.burn });
        any = true;
      }
      if (any) {
        fx.embers(target.x, target.y, { count: 14, color: COLORS.burn });
        fx.ring(target.x, target.y, WILDFIRE_RADIUS, { color: COLORS.burn, duration: 320, width: 3, fromRadius: target.radius });
        sfx.play('burn_ignite', { volume: 0.7, pitch: 0.85, x: target.x }, 60);
      }
    },

    update(dt) {
      if (tracked.size === 0) return;
      for (const [e, t] of tracked) {
        if (!e.alive) {
          tracked.delete(e);
          continue;
        }
        const map = e.statuses;
        const burn = advanceStatuses(map, dt);
        if (burn > 0) {
          try {
            ctx.damage.hitEnemy(e, {
              amount: burn,
              tags: BURN_TAGS.slice(),
              source: 'player',
              isDot: true,
              noTrigger: true,
              label: 'burn',
            });
          } catch (err) {
            console.warn('[combat] burn tick failed', err);
          }
          if (!e.alive) {
            tracked.delete(e);
            continue;
          }
        }
        if (map.size === 0) {
          tracked.delete(e);
          continue;
        }
        drawStatusFx(e, t, dt);
      }
    },

    destroy() {
      tracked.clear();
    },
  };

  /** Small periodic visuals so every status reads at a glance (cheap: a few particles per second). */
  function drawStatusFx(e: EnemyApi, t: Tracked, dt: number): void {
    const map = e.statuses;
    const r = e.radius;
    const burn = map.get('burn');
    if (burn) {
      t.emberT -= dt;
      if (t.emberT <= 0) {
        t.emberT = 0.22 - burn.stacks * 0.025;
        const ox = (Math.random() * 2 - 1) * r * 0.6;
        const oy = (Math.random() * 2 - 1) * r * 0.5;
        fx.embers(e.x + ox, e.y + oy, { count: 1 + Math.ceil(burn.stacks / 2), color: map.has('overheated') ? 0xffe08a : COLORS.burn });
      }
    }
    if (map.has('overheated')) {
      t.smokeT -= dt;
      if (t.smokeT <= 0) {
        t.smokeT = 0.35;
        fx.smoke(e.x, e.y - r * 0.5, { count: 1, color: 0x2a211d, scale: 0.55 });
      }
    }
    const shock = map.get('shock');
    if (shock && !map.has('stun')) {
      t.sparkT -= dt;
      if (t.sparkT <= 0) {
        t.sparkT = 0.42 - shock.stacks * 0.08;
        const a = Math.random() * Math.PI * 2;
        fx.sparks(e.x + Math.cos(a) * r * 0.5, e.y + Math.sin(a) * r * 0.5, a, { count: 1 + shock.stacks, color: COLORS.electric, speed: 160, spread: 1.2, life: 160 });
      }
    }
    if (map.has('stun')) {
      t.stunT -= dt;
      if (t.stunT <= 0) {
        t.stunT = 0.16;
        // Sparks orbiting above the head.
        const a = (performance.now() / 1000) * 9;
        const hx = e.x + Math.cos(a) * r * 0.55;
        const hy = e.y - r - 6 + Math.sin(a) * 6;
        fx.sparks(hx, hy, a + Math.PI / 2, { count: 2, color: 0xfff4a8, speed: 90, spread: 0.6, life: 180 });
      }
    }
    if (map.has('slow') && !burn) {
      t.slowT -= dt;
      if (t.slowT <= 0) {
        t.slowT = 0.4;
        fx.sparks(e.x, e.y + r * 0.6, Math.PI / 2, { count: 1, color: 0xaee6ff, speed: 50, spread: 1.4, life: 300 });
      }
    }
  }

  return api;
}

/** Snapshot the statuses that matter on death (enemies may clear their map when they die). */
export function deathSnapshot(target: EnemyApi): DeathSnapshot {
  const burn = target.statuses.get('burn');
  return {
    burnStacks: burn?.stacks ?? 0,
    burnPotency: burn?.potency ?? 0,
    burnRemaining: burn?.remaining ?? 0,
    overheated: target.statuses.has('overheated'),
  };
}
